import { Injectable, InjectionToken } from '@tanbo/vue-di-plugin';
import { Http } from '@zhfe/common-library';
import { KENIU_URL } from '@/common/config'
import { HttpInterceptorService } from './http-interceptor.service';
import { CookieService } from './cookie.service';
import { BehaviorSubject, Observable } from 'rxjs'

export interface UserInfo {
  uid: string
  username: string
  /** 0-老用户；1-新用户 */
  is_new_user?: number
  /** 注册时间 %Y-%m-%d %H:%M:%S */
  register_time?: string
  avatar_url?: string
  token?: string
  openid?: string
  privliegeName?: string
  privliegeEndTime?: string
  isVip?: boolean
  open_id?: string
  keniu_token?: string
}

export const UserInfoByLogin = new InjectionToken<UserInfo>('userInfo')

export interface Device {
  /** 设备账号 */
  account: string
  /** 系统 */
  os: string
}

export interface RequestParams {
  /** wx的code */
  code: string
  /** 设备信息 */
  device?: Device
}

export interface RequestParamsThird {
  /** 可牛账号token */
  token: string
  /** 登录渠道 */
  channel?: string
  svrid?: string
}

export interface UserInfoKeNiu {
  /** 用户id */
  uid: string
  /** 用户姓名 */
  name: string
  /** 用户头像URL */
  avatar: string
  /** 用户性别, 0未知; 1男性; 2女性 */
  sex: number
}

export interface ResponseDataKeNiu {
  /** 用户信息 */
  user_info: UserInfoKeNiu
  /** 登录的token */
  token: string
  /** 下次调用查询用户token的时间间隔,单位秒	 */
  token_check_time_sec: number
}

export interface ResponseDataThird {
  user_info: UserInfo
  /** token */
  token: string
}

export interface ResponseDataPrivliege {
  /** 权益结束日期 */
  end_date: string
  /** 权益名称 */
  name: string
}

export interface RequestParamsWxCode {
  /** 用户授权code */
  code: string
}

export interface UserInfoKeniu {
  /** open_id用户标识 */
  open_id: string
  /** 性别,0未知; 1男性; 2女性 */
  sex: number
  /** 昵称 */
  name: string
  /** 头像 */
  avatar: string
  /** 登录方式(1-微信, 2-qq, 3-手机) */
  login_type: number
}

export interface TokenInfo {
  /** token */
  token: string
  /** 下次刷新用户token的时间间隔,单位毫秒 */
  check_time_ms: string
}

export interface Data {
  /** 用户详情 */
  user_info: UserInfo
  /** 用户token详情 */
  token_info: TokenInfo
}

export interface ResponseDataKeniu {
  data: Data
}

export interface wxInfoKeniuWx {
  wx_web_open_id: string
}

export interface UserInfoKeniuWx {
  wx_info: wxInfoKeniuWx
}

export interface DataKeniuWx {
  user_info: UserInfoKeniuWx
}

export interface ResponseDataKeniuWx {
  data: DataKeniuWx
}

export interface PayLoginRequestParams {
  /** 登录渠道 */
  channel?: string
  /** 支付登录标识码 */
  code?: string
  /** 微信公众号登录方式传104 */
  keniu_product_id?: string
  /** 可牛用户token */
  token?: string
}

export interface PayLoginUserInfo {
  /** uid */
  uid: string
  /** 用户头像 */
  avatar_url: string
  /** 用户名称 */
  username: string
  /** 是否新用户 */
  is_new_user: number
  /** 注册时间 */
  register_time: string
}

export interface PayLoginResponseData {
  /** token */
  token: string
  /** uid */
  uid: string
  user_info: PayLoginUserInfo
}

@Injectable()
export class UserService {
  userInfo = new BehaviorSubject<UserInfo>({ uid: null, token: null, username: '' })
  constructor (private http: Http, private httpInerceptor: HttpInterceptorService, private cookie: CookieService) {
  }

  updateUserinfo (userinfo: UserInfo) {
    this.httpInerceptor.addCommonRequestFields({
      uid: userinfo.uid + '',
      token: userinfo.token,
      open_id: userinfo.open_id || ''
    })
    this.userInfo.next(userinfo)
    this.cookie.setUserInfo(userinfo)
  }

  async updateUserinfoByCode (code: string) {
    const userInfoKeniu = await this.getAccountLoginWxH5({ code })
    // 可牛用户信息用户token
    const tokenKeniu = userInfoKeniu.data.token_info.token
    // 可牛用户信息用户id
    const openidKeniu = userInfoKeniu.data.user_info.open_id
    // 调用getWxOpenId接口需要使用可牛的用户信息
    this.updateUserinfo({ uid: null, username: '', token: tokenKeniu, open_id: openidKeniu })
    const openid = await this.getWxOpenId()
    // 调用办公资源的服务器，获取真正的用户信息
    const { user_info: userInfo, token } = await this.getApiUserLoginThirdKeniu({ token: tokenKeniu })
    Object.assign(userInfo, { token, openid, keniu_token: tokenKeniu })
    this.updateUserinfo(userInfo)
    this.updatePriliage()
  }

  updatePriliage() {
    let userInfo = this.userInfo.getValue()
    const privliege = {
      privliegeEndTime: '',
      privliegeName: '',
      isVip: false
    }
    return this.getPriliage().then(res => {
      privliege.privliegeName = res.name
      privliege.privliegeEndTime = res.end_date
    }).catch(e => {
      privliege.privliegeName = '普通用户'
    }).finally(() => {
      userInfo = Object.assign({}, userInfo, privliege)
      this.updateUserinfo(userInfo)
    })
  }

  getAccountLoginWxH5 (data: RequestParamsWxCode) {
    return this.http.post<ResponseDataKeniu>(`${KENIU_URL}/account/login/wx_web`, data)
  }

  getWxOpenId () {
    return this.http.post<ResponseDataKeniuWx>(`${KENIU_URL}/usermix/user_info`, {}).then(res => res.data.user_info.wx_info.wx_web_open_id)
  }

  getApiUserLoginThirdKeniu (data: RequestParamsThird) {
    return this.http.post<ResponseDataThird>('/user/login_third/keniu', data)
  }

  getPriliage () {
    return this.http.post<ResponseDataPrivliege>('/user/privliege', {})
  }

  logout () {
    this.updateUserinfo({ uid: null, token: null, username: '' })
  }

  getApiPayloginLoginKeniu (data: PayLoginRequestParams) {
    return this.http.post<PayLoginResponseData>('/paylogin/login_keniu', data)
  }
}
